<template>
  <div class="pt-5 md:p-5">
    <p class="mb-4">{{ $t('log_in_create_account_title') }}</p>
    <register-consent-checkboxes v-model:isTermsAccepted="isTermsAccepted" v-model:isMarketingAllowed="isMarketingAllowed" />
    <nd-alert v-if="errorMessage" type="error" icon="alert" class="mt-2">
      {{ errorMessage }}
    </nd-alert>
  </div>
</template>

<script setup lang="ts">
import RegisterConsentCheckboxes from '@/components/login/register-consent-checkboxes.vue'
import NdAlert from '@/components/common/nd-alert.vue'

import { useAuthStore } from '@/store/auth'
import { useAlertStore } from '@/store/alert'
import { QueryStringKeys } from '@/constants/commons'
import { ResponseCode } from '@/types/service'

import { googleRegister, facebookRegister } from '@/service/login'
import { useLogin } from '@/composables/useAuth'
import { GtmEvent } from '@/types/gtm'

const { t } = useI18n()
const router = useLocaleRouter()

const authStore = useAuthStore()
const alertStore = useAlertStore()
const { handleLoginSuccess } = useLogin()

const isTermsAccepted = ref(true)
const isMarketingAllowed = ref(true)
const isLoading = ref(false)
const errorMessage = ref('')

const accessToken = computed(() => authStore.pendingThirdPartyAuth?.accessToken || '')
const loginType = computed(() => authStore.pendingThirdPartyAuth?.type)
const redirectUrl = computed(() => authStore.pendingThirdPartyAuth?.redirectUrl)
const fallbackUrl = computed(() => authStore.pendingThirdPartyAuth?.fallbackUrl || '/')

const isSubmitDisabled = computed(() => !isTermsAccepted.value || !isMarketingAllowed.value)

const loginTypeMap = {
  facebook: { registerFn: facebookRegister, gtmLabel: 'FB' },
  google: { registerFn: googleRegister, gtmLabel: 'Google' },
}

const getRegisterFunction = () => (loginType.value ? loginTypeMap[loginType.value].registerFn : null)

const handleSubmit = async () => {
  if (isSubmitDisabled.value) return

  try {
    isLoading.value = true

    const registerFunction = getRegisterFunction()
    if (!registerFunction) {
      throw new Error('Invalid login type')
    }

    const res = await registerFunction(accessToken.value, {
      userContract: {
        isTermsAccepted: isTermsAccepted.value,
        isMarketingAllowed: isMarketingAllowed.value,
      },
    })

    if (res.code === ResponseCode.SUCCESS) {
      const registrationType = loginType.value ? loginTypeMap[loginType.value].gtmLabel : ''
      useNuxtApp().$trackEvent({
        event: GtmEvent.ACCOUNT_SIGNED_UP,
        payload: { 'Registration Type': registrationType },
      })

      const targetUrl = redirectUrl.value || fallbackUrl.value
      const safeRedirectUrl = !targetUrl.startsWith('/') || targetUrl.startsWith('//') ? '/' : targetUrl

      await handleLoginSuccess(safeRedirectUrl)
      return
    }
    throw res
  } catch (err: any) {
    if (err instanceof Error) {
      console.error('thirdPartyRegister: error', err.message)
    } else {
      console.error('thirdPartyRegister: response error', err)
    }

    if (loginType.value === 'facebook') {
      alertStore.error(t('member_auth_login_failed'))
      const url = new URL(fallbackUrl.value, location.origin)
      if (redirectUrl.value) {
        url.searchParams.set(QueryStringKeys.REDIRECT_URL, redirectUrl.value)
      }
      router.replace(url.pathname + url.search)
    } else {
      errorMessage.value = t('member_auth_login_failed')
    }
  } finally {
    isLoading.value = false
  }
}

defineExpose({
  isLoading,
  isSubmitDisabled,
  handleSubmit,
})
</script>
